import useReorderCarriers from "@babackapp/private-api-client/client/use-reorder-carriers";
import type { CarrierDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { useMemo, useState } from "react";
import { CarrierListItem } from "./carrier-list-item";

type Props = {
  carriers: CarrierDto[];
  shopId: string;
  canReorder: boolean;
  onEdit: (carrierId: string) => void;
  onDelete: (carrierId: string) => void;
};

/**
 * The list order is the carrier priority: between carriers left tied by tag
 * conditions and pricing scope, the backend picks the highest one (BBS-395).
 */
export function SortableCarrierList({
  carriers: stored,
  shopId,
  canReorder,
  onEdit,
  onDelete,
}: Props) {
  const t = useTranslation();
  const { mutate: reorderCarriers, isPending: isReordering } =
    useReorderCarriers(shopId);

  // Holds the dropped order until the server answers. Without it the row
  // springs back to where it was and only moves once the refetch lands.
  // `over` is the list it was dropped over.
  const [dropped, setDropped] = useState<{
    ids: string[];
    over: CarrierDto[];
  } | null>(null);

  const carriers = useMemo(() => {
    if (!dropped) return stored;

    const byId = new Map(stored.map((carrier) => [carrier.id, carrier]));
    const moved = dropped.ids.flatMap((id) => byId.get(id) ?? []);
    // A carrier added while the call was in flight still shows up, last.
    const rest = stored.filter((carrier) => !dropped.ids.includes(carrier.id));

    return [...moved, ...rest];
  }, [stored, dropped]);

  // Outlives the request on purpose: dropping it on success would flash the old
  // order until the refetch lands. Gives way to the first list the server sends
  // once the call is over, even one that differs (another tab reordered or
  // added a carrier meanwhile): the screen must show the order selection uses.
  // Cleared during render rather than in an effect that would paint first.
  if (dropped && !isReordering && stored !== dropped.over) {
    setDropped(null);
  }

  const isSortable = canReorder && carriers.length > 1;

  // Persisted on drop, as for disapproval reasons: the order is all a drag can
  // express, so there is nothing left to confirm.
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const from = carriers.findIndex((carrier) => carrier.id === active.id);
    const to = carriers.findIndex((carrier) => carrier.id === over.id);
    if (from === -1 || to === -1) return;

    const ids = arrayMove(carriers, from, to).map((carrier) => carrier.id);
    setDropped({ ids, over: stored });
    reorderCarriers(ids, {
      // Put the row back; the mutation's own error toast says why.
      onError: () => setDropped(null),
    });
  };

  return (
    <div className="space-y-4">
      {/* Tells to drag the rows: only for those given a handle. */}
      {isSortable && (
        <p className="text-xs text-muted-foreground">
          {t("carrier.priority.help")}
        </p>
      )}
      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={carriers.map((carrier) => carrier.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-6">
            {carriers.map((carrier) => (
              <SortableCarrierRow
                key={carrier.id}
                carrier={carrier}
                shopId={shopId}
                isSortable={isSortable}
                isDisabled={isReordering}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableCarrierRow({
  carrier,
  shopId,
  isSortable,
  isDisabled,
  onEdit,
  onDelete,
}: {
  carrier: CarrierDto;
  shopId: string;
  isSortable: boolean;
  isDisabled: boolean;
  onEdit: (carrierId: string) => void;
  onDelete: (carrierId: string) => void;
}) {
  const t = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: carrier.id, disabled: !isSortable || isDisabled });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="group flex items-center gap-3"
    >
      {isSortable && (
        <button
          type="button"
          {...listeners}
          {...attributes}
          disabled={isDisabled}
          aria-label={t("carrier.priority.reorder")}
          className="cursor-grab p-1 text-muted-foreground opacity-50 transition-opacity active:cursor-grabbing group-hover:opacity-100 disabled:cursor-not-allowed"
        >
          <GripVertical className="size-4" />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <CarrierListItem
          carrier={carrier}
          shopId={shopId}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}
